import React, { useCallback, useMemo, useRef, useState, useEffect, HtmlHTMLAttributes } from 'react';

import { BoxedDiv, BoxedSpan } from 'psims/react/components/layout';
import { H2, H3 } from 'psims/react/components/typography';
import { ActionCellTD, ActionCellTH, ColumnHeader, Table, TD, TH, TR } from 'psims/react/pages/primary-pages/data-submissions/shared/data-table-components';
import { recordActionFromEnum } from 'psims/models/api/data-submission-record-action';
import { AddButton, IconButton } from 'psims/react/components/button';
import { StorageType } from 'psims/models/ref-data/mso-storage-type';
import { UseConfirm } from '../../../shared/use-confirm';
import { UseFocusedField } from 'psims/react/util/use-focused-field';
import { FocusField, isDataFocusField, isDeleteProductFocusField, MsoProductView } from '../types';
import VisuallyHidden from 'psims/react/components/visually-hidden';
import useFocusable from 'psims/react/util/use-focusable';
import { UseMsoComment } from '../mso-comment/use-mso-comment';
import { MsoStockholdingStorage } from "../types";
import { UseMsoStockholdingStorages } from './use-mso-stockholding-storages';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import Button from 'psims/react/components/button/button';
import ImportFile from 'psims/react/components/icons/import-file';

// ─── Fuel category reference data ─────────────────────────────────────────────

export interface MsoFuelCategory {
  id: number;
  name: string;
}

export const MSO_FUEL_CATEGORIES: MsoFuelCategory[] = [
  { id: 1, name: 'Gasoline' },
  { id: 2, name: 'Diesel' },
  { id: 3, name: 'Kerosene' },
  { id: 4, name: 'Crude' },
  { id: 5, name: 'Unfinished' },
];


// ─── Styled components ────────────────────────────────────────────────────────

const ProductCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #eee;
  width: auto;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
`;

const InfoBox = styled.div`
  background: #f5f7fa;
  border: 1px solid #d6dde6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  color: #333;
  width: 100%;
  box-sizing: border-box;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
  }
`;

const CompactTableWrapper = styled.div`
  overflow-x: none;
  overflow-y: auto;
  max-height: 600px;
  width: 100%;
  box-sizing: border-box;
  display: block;
  h3 { font-size: 16px !important; }

  table { border-collapse: collapse; }

  th { padding: 10px 10px; }
  td { padding: 4px 8px; }

  thead th {
    position: sticky;
    top: 0;
    background: #f5f5f5;
    border-top: solid 1px silver;
  }

  .sticky-col {
    position: sticky;
    left: 0;
    z-index: 4;
    background: white;
    box-shadow: 2px 0 0 #090909;
  }

  thead .sticky-col {
    z-index: 6;
    background: #f5f5f5;
  }

  /* ── Native input reset ── */
  .cell-input {
    width: 100%;
    padding: 3px 6px;
    font-size: 14px;
    height: 28px;
    border: 1px solid #bbb;
    border-radius: 3px;
    background: #fff;
    box-sizing: border-box;
    font-family: inherit;
    outline: none;
    transition: border-color 0.15s;
  }

  .cell-input:focus {
    border-color: #2684ff;
    box-shadow: 0 0 0 2px rgba(38,132,255,0.2);
  }

  .cell-input:disabled {
    background: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }

  /* ── Validation error state ── */
  .cell-input--error {
    border-color: #d0021b !important;
    background-color: #fff8f8;
  }

  .cell-input--error:focus {
    border-color: #d0021b !important;
    box-shadow: 0 0 0 2px rgba(208, 2, 27, 0.2);
  }

  .cell-textarea {
    width: 100%;
    padding: 3px 6px;
    font-size: 14px;
    min-height: 48px;
    border: 1px solid #bbb;
    border-radius: 3px;
    background: #fff;
    box-sizing: border-box;
    font-family: inherit;
    outline: none;
    resize: vertical;
    transition: border-color 0.15s;
  }

  .cell-textarea:focus {
    border-color: #2684ff;
    box-shadow: 0 0 0 2px rgba(38,132,255,0.2);
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface StorageSitesEditorProps {
  deleteCtrl: UseConfirm;
  focusFieldCtrl: UseFocusedField<FocusField>;
  forceErrors: boolean;
  isDisabled: boolean;
  products: Array<MsoProductView>;
  storageTypes: Array<StorageType>;
  storageSitesCtrl: UseMsoStockholdingStorages;
  isRefiner: boolean;
  msoCommentAnnualActivities: any[];
  /** Optional override list of fuel categories (defaults to MSO_FUEL_CATEGORIES) */
  fuelCategories?: MsoFuelCategory[];
  onCommentChange?: (fuelCategory: number, comment: string) => void;
  caseId?: number | string;
}

interface FuelCategoryTableProps extends StorageSitesEditorProps {
  fuelCategory: MsoFuelCategory;
}

/** Fuel categories that show the "entitled capacity" column group */
const ENTITLED_CAPACITY_CATEGORY_IDS = new Set([1, 2, 3]); // Gasoline, Diesel, Kerosene

/**
 * Gasoline / Diesel / Kerosene → 17 cols (all columns)
 * Crude / Unfinished           →  9 cols (cols 1-8 + delete action only)
 */
const getTotalColumns = (showOtherCompaniesColumns: boolean) =>
  showOtherCompaniesColumns ? 17 : 9;

// ─── StorageSitesEditor ───────────────────────────────────────────────────────

const StorageSitesEditor = (props: StorageSitesEditorProps) => {
  const vm = useStorageSitesEditorVM(props);

  const handleExport = useCallback(() => {
    const fuelCategoryName = (id?: number) =>
      MSO_FUEL_CATEGORIES.find(fc => fc.id === id)?.name ?? '';

    const rows = props.storageSitesCtrl.updateStorages
      .filter(s => recordActionFromEnum(s.data.recordAction) !== 'Delete')
      .map(({ data }) => {
        const showOtherCols = ENTITLED_CAPACITY_CATEGORY_IDS.has(data.storageMsoFuelCategory ?? 0);

        const base = {
          'Fuel Category':              fuelCategoryName(data.storageMsoFuelCategory ?? undefined),
          'Terminal Name':              data.terminalName ?? '',
          'Terminal Location':          data.terminalLocation ?? '',
          'Co-owned':                   data.terminalCoOwned === true ? 'Yes' : data.terminalCoOwned === false ? 'No' : '',
          'Terminal Owner(s)':          data.terminalOwners ?? '',
          'Terminal Operator':          data.terminalOperator ?? '',
          'Physical Storage Cap. (ML)': data.physicalStorageCapacity ?? '',
          'Receive Products':           data.receiveProducts ?? '',
          'Distribute Products':        data.distributeProducts ?? '',
        };

        if (!showOtherCols) return base;

        return {
          ...base,
          // Other companies columns
          'Other Company Name':                    data.nameOfOtherCompany ?? '',
          'Other Company Arrangement Type':        data.typeOfArrangementTxt ?? '',
          'Other Company Expiry Date':             data.expiryDate ? new Date(data.expiryDate).toLocaleDateString('en-AU') : '',
          'Capacity Reserved for Company (ML)':    data.capacityReservedForCompany ?? '',
          'Storage Price Offered to Company ($/L)':data.storagePriceThatWouldBeOffered ?? '',
          // Entitled capacity columns
          'Entitled Arrangement Type':             data.typeOfArrangementEntitledCapacityTxt ?? '',
          'Capacity Reserved for You (ML)':        data.capacityReservedForYou ?? '',
          'Storage Price Offered ($)':             data.storagePriceOffered ?? '',
        };
      });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Storage Sites');

    worksheet['!cols'] = [
      { wch: 14 }, // Fuel Category
      { wch: 25 }, // Terminal Name
      { wch: 35 }, // Terminal Location
      { wch: 10 }, // Co-owned
      { wch: 30 }, // Terminal Owners
      { wch: 25 }, // Terminal Operator
      { wch: 22 }, // Physical Storage Cap
      { wch: 20 }, // Receive Products
      { wch: 20 }, // Distribute Products
      { wch: 28 }, // Other Company Name
      { wch: 28 }, // Other Company Arrangement
      { wch: 18 }, // Expiry Date
      { wch: 28 }, // Capacity Reserved Company
      { wch: 30 }, // Storage Price Company
      { wch: 28 }, // Entitled Arrangement
      { wch: 26 }, // Capacity Reserved You
      { wch: 22 }, // Storage Price You
    ];

    const fileName = props.caseId
  ? `${props.caseId}_Storage_Sites.xlsx`
  : 'MSO_Storage_Sites.xlsx';

XLSX.writeFile(workbook, fileName);
  }, [props.storageSitesCtrl.updateStorages]);

  return (
    <BoxedDiv box={{ flex: 'column', marginLeft: 0, marginRight: 0 }}>
      <BoxedDiv box={{ marginTop: 0, marginLeft: 0, marginRight: 0 }}>
        <H2>Storage</H2>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button  $kind="ghost"
          onClick={handleExport}
          disabled={props.storageSitesCtrl.updateStorages.filter(
            s => recordActionFromEnum(s.data.recordAction) !== 'Delete'
          ).length === 0}
          
        >
           <BoxedSpan box={{marginRight: 1}}><ImportFile size="sm"/></BoxedSpan> Export to excel
        </Button>
      </div>
        <InfoBox>
          <p style={{ marginRight: 0 }}>{vm.labels.header}</p>
        </InfoBox>
      </BoxedDiv>
{/*
<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button  $kind="ghost"
          onClick={handleExport}
          disabled={ctrl.rows.length === 0}
        
         >
           <BoxedSpan box={{marginRight: 1}}><ImportFile size="sm"/></BoxedSpan> Export to excel
        </Button>
      </div>
*/} 
      {/* Export button */}
      

      <BoxedDiv box={{ marginTop: 2 }}>
        {vm.visibleFuelCategories.map(fc => (
          <FuelCategoryTable
            key={fc.id}
            {...props}
            fuelCategory={fc}
            onCommentChange={props.onCommentChange}
          />
        ))}
      </BoxedDiv>
    </BoxedDiv>
  );
};

function useStorageSitesEditorVM({
  isRefiner,
  storageSitesCtrl,
  fuelCategories = MSO_FUEL_CATEGORIES,
}: StorageSitesEditorProps) {
  const labels = useMemo(() => ({
    header: isRefiner
      ? 'This section outlines the storage information for MSO products owned or acquired through a leasing arrangement by your entity at a point in time at the end of the calendar year.'
      : 'This section outlines the storage information for MSO products owned or acquired through a leasing arrangement at a point of time at the end of the calendar year.',
  }), [isRefiner]);

  // Only show fuel category groups that have at least one storage record (or all when not disabled)
  const visibleFuelCategories = useMemo(() => {
    const usedCategoryIds = new Set(
      storageSitesCtrl.updateStorages
        .filter(s => recordActionFromEnum(s.data.recordAction) !== 'Delete')
        .map(s => s.data.storageMsoFuelCategory)
        .filter((id): id is number => id != null)
    );
    // Show a category if it has existing data OR we always show all (consumer can adjust this logic)
    return fuelCategories.filter(fc => usedCategoryIds.has(fc.id) || true);
  }, [fuelCategories, storageSitesCtrl.updateStorages]);

  return { labels, visibleFuelCategories };
}

// ─── FuelCategoryTable ────────────────────────────────────────────────────────

const FuelCategoryTable = (props: FuelCategoryTableProps) => {
  const { fuelCategory } = props;
  const showOtherCompanies = ENTITLED_CAPACITY_CATEGORY_IDS.has(fuelCategory.id);
  const showEntitled = showOtherCompanies; // entitled cols only shown when other-companies cols are shown
  const previousYear = new Date().getFullYear() - 1;
  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      <BoxedDiv box={{ marginBottom: 4 }}>
        <ProductCard>
          <H3 style={{paddingBottom:10}}>Storage - {fuelCategory.name}</H3>
          <CompactTableWrapper>
            <Table caption={`Storage for ${fuelCategory.name}`}>
              <thead>
                <TR>
                  <ColumnHeader className="sticky-col" label="Terminal Name" rowSpan={2} fixedWidth='200px'  />
                  <ColumnHeader label="Terminal location (full address and state)" rowSpan={2} fixedWidth='200px' />
                  <ColumnHeader label="Is the Terminal co-owned? [Y/N]" rowSpan={2} fixedWidth='100px' />
                  <ColumnHeader label="Terminal owner(s) and ownership share" rowSpan={2} fixedWidth='200px' Help ={'Specify company name and (if known) ownership percentage share of each company next to the name in brackets and % sign. For example: company name (ownership share %)'} />
                  <ColumnHeader label="Terminal operator" rowSpan={2} fixedWidth='200px' />
                  <ColumnHeader label="Physical storage capacity (ML)²" rowSpan={2} fixedWidth='100px' Help={'Physical storage capacity is the total volume of product the Terminal could hold.'} />
                  <ColumnHeader label="All infrastructure used to:" colSpan={showOtherCompanies ? 2 : 3} Help={'Including, though not limited to, pipeline(s), truck and/or rail gantries, port etc. whether or not owned by the Terminal owner(s).'} />
                  {showOtherCompanies && (
                    <ColumnHeader label="If other companies were using the Terminal:" colSpan={5}  Help={`If you own the Terminal and other companies were using it on 31 December ${previousYear}, please fill in this section.`}/>
                  )}
                  {showEntitled && (
                    <ColumnHeader label="If you are entitled to capacity at the Terminal:" colSpan={4} />
                  )}
                </TR>
                <TR>
                  <ColumnHeader label="Receive products" fixedWidth='150px' />
                  <ColumnHeader label="Distribute products" fixedWidth='180px' />
                  {showOtherCompanies && <>
                    <ColumnHeader label="Name of other company" fixedWidth='250px' />
                    <ColumnHeader label="Type of arrangements" fixedWidth='200px' />
                    <ColumnHeader label="Expiry date or unlimited" />
                    <ColumnHeader label="Capacity reserved for company (ML)" fixedWidth='150px' Help={'Specify the means by which the company uses the Terminal using one of the following defined terms, or if your arrangements do not fit within any of the categories please provide a description of the arrangement:- Joint Terminal - (evergreen agreement) - the non-owner has the same access to the Terminal as the owner(s), contributes to the operating costs, neither company has reserved capacity and fuel is comingled.- Hosting - the non-owner (guest) retains title in the fuel which is co-mingled in the host\'s tanks. The guest and their customers take delivery as required. They remain customers of the company, even though they are taking fuel from the host\'s Terminal. The guest pays a hosting fee and does not contribute to operating costs.- Leasing - lessee has access to the specific tank(s). Lessee pays a lease fee and does not contribute to operating costs.'} />
                    <ColumnHeader label="Storage price that would be offered ($/L)" fixedWidth='180px' />
                  </>}
                  {showEntitled && <>
                    <ColumnHeader label="Type of arrangements" />
                    <ColumnHeader label="Capacity reserved for you (ML)" fixedWidth='150px' Help={`If it is not a contracted amount, this figure should be based on throughput then converted to a percentage of the available capacity and reported as the corresponding ML amount. Alternatively, if neither of those options are possible, this figure can be listed as a yearly average for the ${previousYear} calendar year. Please specify in the notes section which method you have used.`} />
                    <ColumnHeader label="Storage price offered ($)" fixedWidth='150px' />
                  </>}
                  <ColumnHeader label="" />
                </TR>
              </thead>
              <tbody>
                <FuelCategorySection
                  {...props}
                  fuelCategory={fuelCategory}
                  showEntitledCapacityColumns={showOtherCompanies}
                  onCommentChange={props.onCommentChange}
                />
              </tbody>
            </Table>
          </CompactTableWrapper>
        </ProductCard>
      </BoxedDiv>
    </div>
  );
};

// ─── FuelCategorySection ──────────────────────────────────────────────────────

interface FuelCategorySectionProps {
  deleteCtrl: UseConfirm;
  focusFieldCtrl: UseFocusedField<FocusField>;
  forceErrors: boolean;
  isDisabled: boolean;
  products: Array<MsoProductView>;
  storageTypes: Array<StorageType>;
  storageSitesCtrl: UseMsoStockholdingStorages;
  commentsCtrl?: UseMsoComment;
  fuelCategory: MsoFuelCategory;
  showEntitledCapacityColumns: boolean;
  msoCommentAnnualActivities: any[];
  onCommentChange?: (fuelCategory: number, comment: string) => void;
}

const FuelCategorySection = (props: FuelCategorySectionProps) => {
  const vm = useFuelCategorySectionVM(props);

  const [comment, setComment] = useState(vm.initialComment);
  useEffect(() => { setComment(vm.initialComment); }, [vm.initialComment]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setComment(value);
    props.onCommentChange?.(props.fuelCategory.id, value);
  };

  const totalCols = getTotalColumns(props.showEntitledCapacityColumns);

  if (vm.hideSection) return null;

  return (
    <>
      {vm.rowData.map((row, index) => {
        if (row.kind === 'data') {
          return (
            <TR key={row.data.data._key} $isOdd={index % 2 === 1}>
              <DataRow
                {...props}
                data={row.data}
                index={index}
                product={vm.productForRow(row.data.data.msoProductId)}
                showEntitledCapacityColumns={props.showEntitledCapacityColumns}
                onDelete={(message, title, confirm) =>
                  props.deleteCtrl.requestConfirmation(message, title, confirm)
                }
              />
            </TR>
          );
        }
        return null;
      })}

      {vm.showAddButton && (
        <TR>
          <TD colSpan={totalCols}>
            <AddButton
              label="Add storage site"
               onClick={() => vm.storageSitesCtrl.addStorage({
                msoProductId: vm.defaultMsoProductId,
                storageMsoFuelCategory: vm.fuelCategory.id,
              })}
            >
              Add storage
            </AddButton>
          </TD>
        </TR>
      )}

      <TR>
        <TD colSpan={6}>
          <label
            htmlFor={`comments_fc_${vm.fuelCategory.id}`}
            style={{ display: 'block', fontSize: 13, marginBottom: 10, color: '#000', fontWeight: 'bold' }}
          >
            Comments for {vm.fuelCategory.name}
          </label>
          <textarea
            id={`comments_fc_${vm.fuelCategory.id}`}
            className="cell-textarea"
            value={comment}
            disabled={props.isDisabled}
            onChange={handleCommentChange}
             maxLength={4000}
          />
          <div style={{ textAlign: 'right', fontSize: '13px', color: '#666', marginTop: 4 }}>
      {comment.length} of 4000 characters used
    </div>
        </TD>
      </TR>
    </>
  );
};

function useFuelCategorySectionVM({
  fuelCategory,
  isDisabled,
  products,
  storageSitesCtrl,
  msoCommentAnnualActivities,
}: FuelCategorySectionProps) {

  // All non-deleted storages for this fuel category
  const updateStorages = useMemo(() => {
    return storageSitesCtrl.updateStorages.filter(s =>
      s.data.storageMsoFuelCategory === fuelCategory.id &&
      recordActionFromEnum(s.data.recordAction) !== 'Delete'
    );
  }, [fuelCategory.id, storageSitesCtrl.updateStorages]);

  // Resolve MsoProductView for a given msoProductId (used by DataRow for expiry logic)
  const productForRow = useCallback((msoProductId?: number) => {
    return products.find(p => p.id === msoProductId);
  }, [products]);

  /**
   * msoProductId to stamp on new rows added from this fuel-category group.
   * Prefer the product already used in existing rows for this category;
   * fall back to the first active product in the products list.
   */
  const defaultMsoProductId = useMemo(() => {
    const fromExisting = updateStorages[0]?.data.msoProductId;
    if (fromExisting != null) return fromExisting;
    const firstActive = products.find(p => p.productStatus === 'active');
    return firstActive?.id ?? products[0]?.id ?? 0;
  }, [updateStorages, products]);

  const initialComment = useMemo(() => {
    const match = msoCommentAnnualActivities?.find(
      c => c.msoStorageFuelCategoryType === fuelCategory.id
    );
    return match?.reporterComments || '';
  }, [fuelCategory.id, msoCommentAnnualActivities]);

  const rowData = useMemo(() => {
    let data = [
      ...updateStorages.map(s => ({ kind: 'data' as const, data: s })),
      { kind: 'add_button' as const },
    ];
    if (isDisabled && data.length === 1) {
      data = [{
        data: {
          data: {
            _id: Symbol(),
              _key: `empty_fc_${fuelCategory.id}`,
              storageMsoFuelCategory: fuelCategory.id,
              dataSubmissionId: 0,
              msoProductId: 0,
          },
          validations: {},
        },
        kind: 'data' as const,
      }, ...data];
    }
    return data;
  }, [isDisabled, fuelCategory, updateStorages]);

  // Hide section only when disabled AND no rows exist for this category
  const hideSection = useMemo(() =>
    isDisabled && updateStorages.length === 0,
    [isDisabled, updateStorages.length]
  );

  const showAddButton = useMemo(() =>
    !isDisabled,
    [isDisabled]
  );

  return {
    defaultMsoProductId,
    fuelCategory,
    hideSection,
    initialComment,
    isDisabled,
    productForRow,
    rowData,
    showAddButton,
    storageSitesCtrl,
    updateStorages,
  };
}

// ─── DataRow ──────────────────────────────────────────────────────────────────

type UpdateStorageView = UseMsoStockholdingStorages['updateStorages'][number];

interface DataRowProps {
  data: UpdateStorageView;
  focusFieldCtrl: UseFocusedField<FocusField>;
  forceErrors: boolean;
  index: number;
  isDisabled: boolean;
  onDelete: UseConfirm['requestConfirmation'];
  product: MsoProductView | undefined;
  showEntitledCapacityColumns: boolean;
  storageSitesCtrl: UseMsoStockholdingStorages;
  storageTypes: Array<StorageType>;
}

const toDateInputValue = (val?: string | Date | null) => {
  if (!val) return '';
  const d = typeof val === 'string' ? new Date(val) : val;
  return d.toISOString().split('T')[0];
};

const DataRow = (props: DataRowProps) => {
  const vm = useDataRowVM(props);
  const { showEntitledCapacityColumns } = props;

  const terminalNameRef = useRef<HTMLInputElement>(null);
  const terminalLocationRef = useRef<HTMLInputElement>(null);
  const terminalCoOwnedRef = useRef<HTMLSelectElement>(null);
  const terminalOwnersRef = useRef<HTMLInputElement>(null);
  const terminalOperatorRef = useRef<HTMLInputElement>(null);
  const physicalStorageCapacityRef = useRef<HTMLInputElement>(null);
  const receiveProductsRef = useRef<HTMLInputElement>(null);
  const distributeProductsRef = useRef<HTMLInputElement>(null);



  useEffect(() => {
    if (!isDataFocusField(props.focusFieldCtrl.focusedField)) return;
    const f = props.focusFieldCtrl.focusedField;
    if (f._id !== vm.data._id) return;
    if (f.field === 'terminalName') terminalNameRef.current?.focus();
    if (f.field === 'terminalLocation') terminalLocationRef.current?.focus();
    if (f.field === 'terminalCoOwned') terminalCoOwnedRef.current?.focus();
    if (f.field === 'terminalOwners') terminalOwnersRef.current?.focus();
    if (f.field === 'terminalOperator') terminalOperatorRef.current?.focus();
    if (f.field === 'physicalStorageCapacity') physicalStorageCapacityRef.current?.focus();
    //receiveProducts
    if  (f.field === 'receiveProducts') receiveProductsRef.current?.focus();
    //distributeProducts
    if  (f.field === 'distributeProducts') distributeProductsRef.current?.focus();
    
    
  }, [props.focusFieldCtrl.focusedField, vm.data._id]);

  const blur = (field: keyof MsoStockholdingStorage) =>
    (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      vm.updateField(field, e.target.value);

  const blurNumber = (field: keyof MsoStockholdingStorage) =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value;
      vm.updateField(field, v === '' ? undefined : Number(v));
    };

  const blurBool = (field: keyof MsoStockholdingStorage) =>
    (e: React.FocusEvent<HTMLSelectElement>) => {
      const v = e.target.value;
      vm.updateField(field, v === 'Yes' ? true : v === 'No' ? false : undefined);
    };

  const hasTerminalNameError     = vm.forceErrors && !!vm.validations.terminalName;
  const hasTerminalLocationError = vm.forceErrors && !!vm.validations.terminalLocation;
  const hasTerminalOperatorError  = vm.forceErrors && !!vm.validations.terminalOperator;
  const hasTerminalOwnerError = vm.forceErrors && !!vm.validations.terminalOwners;
  const hasPhysicalStorageError = vm.forceErrors && !! vm.validations.physicalStorageCapacity;
  const hasReceiveProductsError = vm.forceErrors && !! vm.validations.receiveProducts;
  const hasDistributeProductsError = vm.forceErrors && !! vm.validations.distributeProducts;
  

  const disabled = vm.isDisabled;

  return (
    <>
      {/* 1 Terminal Name — sticky, validated */}
      <TD className="sticky-col">
        <input
          ref={terminalNameRef}
          className={`cell-input${hasTerminalNameError ? ' cell-input--error' : ''}`}
          defaultValue={vm.data.terminalName || ''}
          disabled={disabled}
          aria-label="Terminal name"
          aria-invalid={hasTerminalNameError}
          onBlur={blur('terminalName')}
        />
      </TD>

      {/* 2 Terminal Location — validated */}
      <TD>
        <input
          ref={terminalLocationRef}
          className={`cell-input${hasTerminalLocationError ? ' cell-input--error' : ''}`}
          defaultValue={vm.data.terminalLocation || ''}
          disabled={disabled}
          aria-label="Terminal location"
          aria-invalid={hasTerminalLocationError}
          onBlur={blur('terminalLocation')}
        />
      </TD>

      {/* 3 Co-owned */}
      <TD>
        <select
          ref={terminalCoOwnedRef}
          className="cell-input"
          defaultValue={
            vm.data.terminalCoOwned === true ? 'Yes' :
            vm.data.terminalCoOwned === false ? 'No' : ''
          }
          disabled={disabled}
          aria-label="Terminal co-owned"
          onBlur={blurBool('terminalCoOwned')}
        >
          <option value="">—</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </TD>

      {/* 4 Terminal Owners */}
      <TD>
        <input 
          ref={terminalOwnersRef}
          className={`cell-input${hasTerminalOwnerError ? ' cell-input--error' : ''}`}
          defaultValue={vm.data.terminalOwners || ''}
          disabled={disabled} aria-label="Terminal owners" 
          aria-invalid={hasTerminalOwnerError}
          onBlur={blur('terminalOwners')} />
      </TD>

      {/* 5 Terminal Operator */}
      <TD>
        <input 
          ref={terminalOperatorRef}
          className={`cell-input${hasTerminalOperatorError ? ' cell-input--error' : ''}`}
          defaultValue={vm.data.terminalOperator || ''}
          disabled={disabled} 
          aria-invalid={hasTerminalOperatorError}
          aria-label="Terminal operator" onBlur={blur('terminalOperator')} />
      </TD>

      {/* 6 Physical Storage Capacity hasPhysicalStorageError*/}
      <TD>
        <input 
          ref={physicalStorageCapacityRef}
          className={`cell-input${hasPhysicalStorageError ? ' cell-input--error' : ''}`}
          type="number" 
          defaultValue={vm.data.physicalStorageCapacity ?? ''}
          disabled={disabled} aria-label="Physical storage capacity" 
           aria-invalid={hasPhysicalStorageError}
          onBlur={blurNumber('physicalStorageCapacity')} />
      </TD>

      {/* 7 Receive Products */}
      <TD>
        <input 
          ref={receiveProductsRef}
          className={`cell-input${hasReceiveProductsError ? ' cell-input--error' : ''}`}
          defaultValue={vm.data.receiveProducts || ''}
          aria-invalid={hasReceiveProductsError}
          disabled={disabled} aria-label="Receive products" onBlur={blur('receiveProducts')} />
      </TD>

      {/* 8 Distribute Products */}
      <TD>
        <input 
          className={`cell-input${hasDistributeProductsError ? ' cell-input--error' : ''}`}
          defaultValue={vm.data.distributeProducts || ''}
          disabled={disabled} 
          aria-label="Distribute products" 
          aria-invalid={hasDistributeProductsError}
          onBlur={blur('distributeProducts')} />
      </TD>

      {/* 9 Name of Other Company */}
      {showEntitledCapacityColumns && (
        <TD>
          <input className="cell-input" defaultValue={vm.data.nameOfOtherCompany || ''}
            disabled={disabled} aria-label="Name of other company" onBlur={blur('nameOfOtherCompany')} />
        </TD>
      )}

      {/* 10 Type of Arrangement */}
      {showEntitledCapacityColumns && (
        <TD>
          <input className="cell-input" defaultValue={vm.data.typeOfArrangementTxt || ''}
            disabled={disabled} aria-label="Type of arrangements" onBlur={blur('typeOfArrangementTxt')} />
        </TD>
      )}

      {/* 11 Expiry Date */}
      {showEntitledCapacityColumns && (
        <TD>
          <input className="cell-input" type="date" defaultValue={toDateInputValue(vm.data.expiryDate)}
            disabled={disabled} aria-label="Expiry date" onBlur={blur('expiryDate')} />
        </TD>
      )}

      {/* 12 Capacity Reserved for Company */}
      {showEntitledCapacityColumns && (
        <TD>
          <input className="cell-input" type="number" defaultValue={vm.data.capacityReservedForCompany ?? ''}
            disabled={disabled} aria-label="Capacity reserved for company" onBlur={blurNumber('capacityReservedForCompany')} />
        </TD>
      )}

      {/* 13 Storage Price That Would Be Offered */}
      {showEntitledCapacityColumns && (
        <TD>
          <input className="cell-input" type="number" defaultValue={vm.data.storagePriceThatWouldBeOffered ?? ''}
            disabled={disabled} aria-label="Storage price that would be offered" onBlur={blurNumber('storagePriceThatWouldBeOffered')} />
        </TD>
      )}

      {/* 14 Type of Arrangement (Entitled Capacity) */}
      {showEntitledCapacityColumns && (
        <TD>
          <input className="cell-input" defaultValue={vm.data.typeOfArrangementEntitledCapacityTxt || ''}
            disabled={disabled} aria-label="Type of arrangements (entitled capacity)" onBlur={blur('typeOfArrangementEntitledCapacityTxt')} />
        </TD>
      )}

      {/* 15 Capacity Reserved for You */}
      {showEntitledCapacityColumns && (
        <TD>
          <input className="cell-input" type="number" defaultValue={vm.data.capacityReservedForYou ?? ''}
            disabled={disabled} aria-label="Capacity reserved for you" onBlur={blurNumber('capacityReservedForYou')} />
        </TD>
      )}

      {/* 16 Storage Price Offered */}
      {showEntitledCapacityColumns && (
        <TD>
          <input className="cell-input" type="number" defaultValue={vm.data.storagePriceOffered ?? ''}
            disabled={disabled} aria-label="Storage price offered" onBlur={blurNumber('storagePriceOffered')} />
        </TD>
      )}

      {/* 17 Delete action */}
      <TD>
        {vm.showIndividualDelete && (
          <IconButton
            color='dark-grey'
            icon='trash'
            label='Delete row'
            onClick={() => {
              const isNew = !vm.data.id || recordActionFromEnum(vm.data.recordAction) === 'Create';
              if (isNew) {
                vm.deleteRow();
              } else {
                vm.onDelete(
                  'Are you sure you wish to delete this data?',
                  'Storage will be deleted',
                  vm.deleteRow
                );
              }
            }}
            size='sm'
            styles={{ marginLeft: '6px', marginRight: '-12px', padding: '4px' }}
          />
        )}
      </TD>
    </>
  );
};

function useDataRowVM({
  data,
  focusFieldCtrl,
  forceErrors,
  index,
  isDisabled,
  onDelete,
  product,
  storageSitesCtrl,
}: DataRowProps) {

  const updateField = useCallback(
    (field: keyof MsoStockholdingStorage, val: any) => {
      storageSitesCtrl.updateStorage({ ...data.data, [field]: val });
    },
    [data, storageSitesCtrl]
  );

  const deleteRow = useCallback(() => {
    storageSitesCtrl.deleteStorage(data.data);
  }, [data, storageSitesCtrl]);

const isExpired = useMemo(() => product?.productStatus === 'inactive', [product]);

  const showIndividualDelete = useMemo(() =>
    !isDisabled && !isExpired,
    [isDisabled, isExpired]
  );

  return {
    data: data.data,
    deleteRow,
    forceErrors,
    index,
    isDisabled,
    onDelete,
    showIndividualDelete,
    updateField,
    validations: data.validations,
  };
}

// ─── DeleteProductButton ──────────────────────────────────────────────────────
// (Kept for backward compatibility — may be used outside this file)

interface DeleteProductProps {
  focusedField: unknown;
  onClick: () => any;
  productId: number;
}

const DeleteProductButton = React.forwardRef(({ focusedField, onClick, productId }: DeleteProductProps, ref) => {
  const focusable = useFocusable({
    setFocused: (
      isDeleteProductFocusField(focusedField) &&
      focusedField.kind === 'msoStockholdingStorages' &&
      focusedField.productId === productId
    ),
  });

  const setRef = useCallback((el: HTMLElement | null) => {
    if (typeof ref === 'function') ref(el);
    focusable.setRef(el);
  }, [focusable, ref]);

  return (
    <BoxedSpan box={{ marginLeft: 1 }}>
      <IconButton
        color='dark-grey'
        icon='trash'
        label='Delete all rows for product'
        onClick={onClick}
        ref={setRef}
        size='sm'
      />
    </BoxedSpan>
  );
});

export default StorageSitesEditor;